import React, { useEffect, useRef, useState, useMemo } from "react";
import axios from "axios";
import "../styles/GamePage.scss";
import { buildStyles, CircularProgressbar } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";

const COUNTDOWN = 60;
const EFFECT_DURATION_MS = 1200;
const PUBLIC_URL = process.env.PUBLIC_URL || "";

const GamePage = () => {
  const [score, setScore] = useState(0);
  const [isPopping, setIsPopping] = useState(false);
  const [timeLeft, setTimeLeft] = useState(COUNTDOWN);
  const [isRunning, setIsRunning] = useState(true);

  const [isEffectPlaying, setIsEffectPlaying] = useState(false);
  const [effectPlayId, setEffectPlayId] = useState(0);
  const [submittedUUID, setSubmittedUUID] = useState(null);

  const effectTimerRef = useRef(null);
  const activePointers = useRef(new Set());
  const timerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    if (isRunning) {
      const scrollY = window.scrollY;
      const orig = {
        bodyPos: document.body.style.position,
        bodyTop: document.body.style.top,
        bodyWidth: document.body.style.width,
        bodyOverflow: document.body.style.overflow,
        htmlOverflow: document.documentElement.style.overflow,
      };

      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";

      return () => {
        document.body.style.position = orig.bodyPos;
        document.body.style.top = orig.bodyTop;
        document.body.style.width = orig.bodyWidth;
        document.body.style.overflow = orig.bodyOverflow;
        document.documentElement.style.overflow = orig.htmlOverflow;

        window.scrollTo(0, scrollY);
      };
    }
  }, [isRunning]);

  useEffect(() => {
    const preload = (src) => {
      const img = new Image();
      img.src = src;
    };
    [
      `${PUBLIC_URL}/images/love_1.png`,
      `${PUBLIC_URL}/images/love_2.png`,
      `${PUBLIC_URL}/images/love_3.png`,
      `${PUBLIC_URL}/images/love_4.png`,
      `${PUBLIC_URL}/images/result_1.png`,
      `${PUBLIC_URL}/images/result_2.png`,
      `${PUBLIC_URL}/images/result_3.png`,
      `${PUBLIC_URL}/images/result_4.png`,
      `${PUBLIC_URL}/images/love_effect.gif`,
    ].forEach(preload);
  }, []);

  const triggerPop = () => {
    setIsPopping(false);
    requestAnimationFrame(() => setIsPopping(true));
  };

  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            timerRef.current = null;
            setIsRunning(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isRunning, timeLeft]);

  useEffect(() => {
    if (timeLeft === 0 && score > 0) {
      axios
        .post("https://www.angelface.com.hk/wp-json/anniversary-38/submit", {
          count: score,
        })
        .then((res) => {
          console.log("Submit success:", res.data);
          if (res.data?.uuid) setSubmittedUUID(res.data.uuid);
        })
        .catch((err) => {
          console.error("Submit failed:", err);
        });
    }
  }, [timeLeft, score]);

  const tier = useMemo(() => {
    if (score >= 388) return 4;
    if (score >= 288) return 3;
    if (score >= 188) return 2;
    return 1;
  }, [score]);

  const loveSrc = useMemo(
    () => `${PUBLIC_URL}/images/love_${tier}.png`,
    [tier]
  );
  const resultSrc = useMemo(
    () => `${PUBLIC_URL}/images/result_${tier}.png`,
    [tier]
  );

  const triggerEffectOnce = () => {
    if (isEffectPlaying) return;
    setIsEffectPlaying(true);
    setEffectPlayId(Date.now());
    if (effectTimerRef.current) clearTimeout(effectTimerRef.current);
    effectTimerRef.current = setTimeout(() => {
      setIsEffectPlaying(false);
    }, EFFECT_DURATION_MS);
  };

  const handlePointerDown = (e) => {
    e.preventDefault();
    if (!isRunning || timeLeft <= 0) return;
    activePointers.current.add(e.pointerId);
    if (activePointers.current.size === 1) {
      setScore((s) => s + 1);
      triggerPop();
      triggerEffectOnce();
    }
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const handlePointerUpOrCancel = (e) => {
    activePointers.current.delete(e.pointerId);
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  const handleRestart = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (effectTimerRef.current) {
      clearTimeout(effectTimerRef.current);
      effectTimerRef.current = null;
    }
    activePointers.current.clear();
    setScore(0);
    setTimeLeft(COUNTDOWN);
    setIsRunning(true);
    setIsEffectPlaying(false);
    setSubmittedUUID(null);
  };

  useEffect(() => {
    return () => {
      if (effectTimerRef.current) clearTimeout(effectTimerRef.current);
    };
  }, []);

  return (
    <div className="game-page-container">
      <div className="score-container">
        <img
          className="score-text"
          src={`${PUBLIC_URL}/images/score.png`}
          alt="score"
        />
        <div className="score">{score}</div>
      </div>

      <div className="timer">
        <CircularProgressbar
          maxValue={COUNTDOWN}
          minValue={0}
          value={timeLeft}
          text={`${timeLeft}`}
          styles={buildStyles({
            textSize: "250%",
            pathColor: `#efa3be`,
            textColor: `#efa3be`,
            trailColor: `#ffffff`,
          })}
        />
      </div>

      <div
        className="game-click-area"
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUpOrCancel}
        onPointerCancel={handlePointerUpOrCancel}
        onPointerLeave={handlePointerUpOrCancel}
      >
        <div
          className={`game-button-container ${
            !isRunning || timeLeft === 0 ? "" : ""
          }`}
        >
          {isEffectPlaying && (
            <img
              key={effectPlayId}
              className="game-love-effect"
              src={`${PUBLIC_URL}/images/love_effect.gif?t=${effectPlayId}`}
              alt="effect"
              draggable={false}
            />
          )}

          <img
            className="game-button-background"
            src={`${PUBLIC_URL}/images/game_button_container.png`}
            alt="game-button-container-image"
            // draggable={false}
          />
          <img
            className={`game-button ${isPopping ? "pop" : ""}`}
            src={loveSrc}
            alt="game-button"
            onAnimationEnd={() => setIsPopping(false)}
            draggable={false}
          />
        </div>
      </div>

      {timeLeft === 0 ? (
        <>
          <img
            className="result"
            src={resultSrc}
            alt="result"
            draggable={false}
          />
          <div className="result-button-container">
            <div className="restart-button-container">
              <div
                className="restart-button"
                onClick={handleRestart}
                draggable={false}
              >
                <img
                  src={`${PUBLIC_URL}/images/restart.png`}
                  alt="restart"
                  className="icon"
                ></img>{" "}
                重試
              </div>
            </div>
            {score >= 188 && (
              <span className="whatsapp-button-container">
                <a
                  href={`https://api.whatsapp.com/send?phone=85281200088&text=你好，我已完成遊戲並按到 ${score} 次心心，想領取療程現金券。%0Ahttps://www.angelface.com.hk/anniversary-38-result?uuid=${submittedUUID}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <div className="whatsapp-button" draggable={false}>
                    <img
                      src={`${PUBLIC_URL}/images/whatsapp.png`}
                      alt="whatsapp"
                      className="icon"
                    ></img>{" "}
                    聯絡專人領獎
                  </div>
                  {/* <img
                    src={`${PUBLIC_URL}/images/whatsapp_button.png`}
                    alt="whatsapp"
                    draggable={false}
                    className="whatsapp-button"
                  /> */}
                </a>
              </span>
            )}
          </div>
        </>
      ) : (
        <>
          <img
            className="click-now"
            src={`${PUBLIC_URL}/images/click_now.png`}
            alt="click-now"
            draggable={false}
          />
          <img
            className="reward"
            src={`${PUBLIC_URL}/images/rewards.png`}
            alt="rewards"
            draggable={false}
          />
        </>
      )}
      {timeLeft === 0 && (
        <span className="tnc">
          <sup>*</sup>只限新客戶 。<br/>
          <sup>*</sup>禮券不可疊加, 每位只可領獎一次<br/>
          <sup>*</sup>以每電話號碼單位計算，只可以挑戰三次，以最高分數為最終獲勝。<br/>
          <sup>*</sup>此免費體驗療程必須於發出日期日起計，三個月內使用，逾期無效，並不作補發。<br/>
          <sup>*</sup>ANGEL FACE保留最終決定權。
        </span>
      )}
    </div>
  );
};

export default GamePage;
